import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { PillTag } from '@/components/pill-tag';
import { Button } from '@/components/ui/button';
import { Link } from '@tanstack/react-router';
import { container, fadeIn, fadeUp } from '@/constants/motion';
import { ArrowRight, TrendingUp } from 'lucide-react';
export const LandingPageHero = () => {
    const { t } = useTranslation('marketing');
    const heroMetrics = React.useMemo(() => [
        {
            label: t('landing.dashboardMock.metrics.totalScans'),
            value: '1,248',
            change: '+12%',
        },
        {
            label: t('landing.dashboardMock.metrics.uniqueVisitors'),
            value: '873',
            change: '+8%',
        },
        {
            label: t('landing.dashboardMock.metrics.topLinkCtr'),
            value: '42%',
            change: '+5%',
        },
    ], [t]);
    return (<motion.section className="relative overflow-hidden border-b bg-linear-to-b from-primary/6 via-background to-background" initial="hidden" animate="visible" variants={container}>
            {/* Decorative blobs */}
            <div className="pointer-events-none absolute -top-32 -right-32 h-125 w-125 rounded-full bg-primary/8 blur-3xl"/>
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-primary/5 blur-3xl"/>

            <div className="relative mx-auto max-w-6xl px-4 py-20 md:py-28">
                <div className="grid items-center gap-12 md:grid-cols-2">
                    {/* ================= LEFT ================= */}
                    <motion.div className="space-y-7" variants={container}>
                        <motion.div variants={fadeUp}>
                            <PillTag label={t('landing.hero.pill')}/>
                        </motion.div>

                        <div className="space-y-1">
                            <motion.h1 variants={fadeUp} className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                                {t('landing.hero.title.main')}.
                            </motion.h1>
                            <motion.h1 variants={fadeUp} className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl text-primary">
                                {t('landing.hero.title.highlight')}
                            </motion.h1>
                        </div>

                        <motion.p variants={fadeUp} className="text-base text-muted-foreground md:text-lg max-w-lg leading-relaxed">
                            {t('landing.hero.subtitle')}
                        </motion.p>

                        <motion.div variants={fadeUp} className="pt-1">
                            <div className="flex w-full flex-col sm:flex-row gap-3">
                                <Button asChild size="lg" className="rounded-full px-8 gap-2 shadow-lg shadow-primary/20">
                                    <Link to="/sign-up" search={{ redirect: '/console' }}>
                                        {t('get_started', { ns: 'common' })}
                                        <ArrowRight className="h-4 w-4"/>
                                    </Link>
                                </Button>
                                <Button asChild variant="outline" size="lg" className="rounded-full px-8">
                                    <Link to="/sign-in" search={{ redirect: '/console' }}>
                                        {t('sign_in', { ns: 'common' })}
                                    </Link>
                                </Button>
                            </div>
                        </motion.div>

                        <motion.p variants={fadeIn} className="text-xs text-muted-foreground">
                            {t('landing.hero.trustLine', { count: 2 })}
                        </motion.p>
                    </motion.div>

                    {/* ================= RIGHT (Dashboard Mock) ================= */}
                    <motion.div variants={fadeUp} className="rounded-2xl border bg-card/80 backdrop-blur-sm p-5 shadow-2xl shadow-primary/10 ring-1 ring-border/60">
                        <div className="mb-5 flex items-center justify-between">
                            <div>
                                <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
                                    {t('landing.dashboardMock.activeCampaignLabel')}
                                </p>
                                <p className="mt-0.5 text-sm font-semibold">
                                    {t('landing.dashboardMock.exampleCampaignName')}
                                </p>
                            </div>
                            <span className="flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"/>
                                Active
                            </span>
                        </div>

                        <motion.div className="grid gap-3 sm:grid-cols-3" variants={container} initial="hidden" animate="visible">
                            {heroMetrics.map((item) => (<motion.div key={item.label} variants={fadeIn} className="rounded-xl border bg-background p-3">
                                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                                        {item.label}
                                    </p>
                                    <p className="mt-1 text-xl font-bold">{item.value}</p>
                                    <span className="mt-0.5 inline-flex items-center gap-0.5 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                                        <TrendingUp className="h-3 w-3"/>
                                        {item.change}
                                    </span>
                                </motion.div>))}
                        </motion.div>

                        <div className="mt-5">
                            <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground mb-2.5">
                                {t('landing.dashboardMock.linksLabel')}
                            </p>

                            <motion.div className="space-y-2" variants={container} initial="hidden" animate="visible">
                                {[
            {
                name: t('landing.dashboardMock.exampleLinks.whatsapp'),
                url: 'wa.me/adora',
                active: true,
            },
            {
                name: t('landing.dashboardMock.exampleLinks.facebook'),
                url: 'facebook.com/adora',
                active: true,
            },
            {
                name: t('landing.dashboardMock.exampleLinks.tiktok'),
                url: 'tiktok.com/@adora',
                active: false,
            },
        ].map((link) => (<motion.div key={link.name} variants={fadeIn} className="flex items-center justify-between rounded-lg border bg-background px-3 py-2.5">
                                        <div>
                                            <p className="text-xs font-semibold">{link.name}</p>
                                            <p className="text-[11px] text-muted-foreground truncate">{link.url}</p>
                                        </div>
                                        <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${link.active ? 'bg-primary' : 'bg-muted'}`}>
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${link.active ? 'translate-x-4' : 'translate-x-0'}`}/>
                                        </span>
                                    </motion.div>))}
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </motion.section>);
};
LandingPageHero.displayName = 'LandingPageHero';
